import { formatHumanName } from '@medplum/core';
import type { Appointment, Patient } from '@medplum/fhirtypes';
import { patientDisplayLabelFromReference } from '@/pages/appointments/utils/fhirHelpers';

export interface SessionContactInfo {
  patientId: string;
  name: string;
  initials: string;
  phones: string[];
  emails: string[];
  address?: string;
  isPrimary?: boolean;
}

function patientInitials(patient: Patient): string {
  const given = patient.name?.[0]?.given?.[0]?.[0] ?? '';
  const family = patient.name?.[0]?.family?.[0] ?? '';
  return `${given}${family}`.toUpperCase() || '?';
}

/** Avatar initials for a roster entry whose Patient resource is not loaded. */
export function initialsFromName(name: string): string {
  const initials = name
    .split(/\s+/)
    .map((word) => word[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return initials || '?';
}

function formatPatientAddress(patient: Patient): string | undefined {
  const addr = patient.address?.[0];
  if (!addr) return undefined;
  const parts = [
    ...(addr.line ?? []),
    [addr.city, addr.state, addr.postalCode].filter(Boolean).join(', '),
  ].filter(Boolean);
  const joined = parts.join(', ').trim();
  return joined || undefined;
}

export function mapPatientToSessionContact(
  patient: Patient,
  options?: { isPrimary?: boolean },
): SessionContactInfo {
  const phones = (patient.telecom ?? [])
    .filter((t) => t.system === 'phone' && t.value?.trim())
    .map((t) => t.value!.trim());
  const emails = (patient.telecom ?? [])
    .filter((t) => t.system === 'email' && t.value?.trim())
    .map((t) => t.value!.trim());

  return {
    patientId: patient.id ?? '',
    name: formatHumanName(patient.name?.[0]) || 'Client',
    initials: patientInitials(patient),
    phones,
    emails,
    address: formatPatientAddress(patient),
    isPrimary: options?.isPrimary,
  };
}

/** Patient ids on the appointment, in participant order. */
export function rosterPatientIdsFromAppointment(
  appointment: Appointment | null | undefined,
): string[] {
  const ids = (appointment?.participant ?? [])
    .map((p) => p.actor?.reference)
    .filter((ref): ref is string => Boolean(ref?.startsWith('Patient/')))
    .map((ref) => ref.replace('Patient/', ''));
  return [...new Set(ids)];
}

/**
 * One contact per session participant, in roster order. Participants whose `Patient` is
 * still loading or unreadable keep a placeholder row rather than dropping off the list,
 * so the participants panel and the treatment-plan picker always agree with the roster.
 */
export function buildSessionContacts(params: {
  appointment: Appointment | null | undefined;
  loadedPatients: Patient[];
  primaryPatientId?: string;
}): SessionContactInfo[] {
  const { appointment, loadedPatients, primaryPatientId } = params;

  const loadedById = new Map<string, Patient>();
  for (const patient of loadedPatients) {
    if (patient.id) loadedById.set(patient.id, patient);
  }
  const displayById = new Map(
    (appointment?.participant ?? [])
      .filter((p) => p.actor?.reference?.startsWith('Patient/'))
      .map((p) => [p.actor!.reference!.replace('Patient/', ''), p.actor?.display] as const),
  );

  const rosterIds = rosterPatientIdsFromAppointment(appointment);
  const ids = rosterIds.length > 0 ? rosterIds : primaryPatientId ? [primaryPatientId] : [];

  return ids.map((id) => {
    const loaded = loadedById.get(id);
    if (loaded) {
      return mapPatientToSessionContact(loaded, { isPrimary: id === primaryPatientId });
    }
    const name = patientDisplayLabelFromReference(`Patient/${id}`, displayById.get(id));
    return {
      patientId: id,
      name,
      initials: initialsFromName(name),
      phones: [],
      emails: [],
      isPrimary: id === primaryPatientId,
    };
  });
}
